import { ensureError } from '@passionware/platform-js';
import store from '../../../../store';
import { BOOKING_ACTIONS } from '../../../../store/v1/bookings/bookings.constants';
import { EXPENSES_ACTIONS } from '../../../../store/v1/expenses/expenses.constants';
import { BookingApi } from 'v5/api/booking/booking.api';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import {
  awaitReportSystemEffects,
  getSystemEffectsForBookingUpdate,
  getSystemEffectsForExpenseUpdate,
  getSystemEffectsForProductionResourcingUpdate
} from 'v5/services/internal/systemEffects/productionResourcingSystemEffects';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { BookingMutationService } from './BookingMutationService';

export function createBookingMutationService(
  config: WithServices<
    [WithMessageService, WithErrorService, WithToastService]
  > & {
    api: BookingApi;
  }
): BookingMutationService {
  const {
    api,
    services: { messageService, errorService, toastService }
  } = config;
  const interceptor = errorService.withCategory('BookingMutationService');

  const runWithInvoiceToasts = async <T>(
    options: { bypassToasts?: boolean } | undefined,
    fn: () => Promise<T>,
    successMessage: string,
    errorMessage: string
  ): Promise<T> => {
    try {
      const result = await fn();
      if (!options?.bypassToasts) {
        toastService.open({ type: 'success', message: successMessage });
      }
      return result;
    } catch (e) {
      if (!options?.bypassToasts) {
        toastService.open({
          type: 'error',
          message: errorMessage || ensureError(e).message
        });
      }
      throw e;
    }
  };

  return {
    updateBooking: interceptor.wrap(async request => {
      const result = await api.updateItem(request.id, request.data);
      await awaitReportSystemEffects(
        messageService,
        getSystemEffectsForBookingUpdate(request.id)
      );
      return result;
    }),
    // Sending stamps confirmation_sent_at, which drives confirmation pills and docs cells.
    sendBookingConfirmations: async request => {
      const { affectedBookingIds, ...apiRequest } = request;
      await api.sendBookingConfirmations(apiRequest);
      if ('productionId' in apiRequest) {
        await awaitReportSystemEffects(
          messageService,
          getSystemEffectsForProductionResourcingUpdate({
            productionId: apiRequest.productionId,
            bookingIds: affectedBookingIds
          })
        );
        return;
      }
      await awaitReportSystemEffects(
        messageService,
        getSystemEffectsForBookingUpdate(apiRequest.bookingId)
      );
    },
    archiveBookings: interceptor.wrap(async request => {
      try {
        await api.bulkArchive(request);

        // Report system effects for successfully archived bookings

        await awaitReportSystemEffects(
          messageService,
          request.ids.flatMap(id => getSystemEffectsForBookingUpdate(id))
        );
      } catch (error) {
        console.error('Failed to archive bookings:', error);
        throw new Error('Failed to archive bookings');
      }
    }),

    unarchiveBookings: interceptor.wrap(async request => {
      try {
        await api.bulkUnarchive(request);

        // Report system effects for successfully unarchived bookings

        await awaitReportSystemEffects(
          messageService,
          request.ids.flatMap(id => getSystemEffectsForBookingUpdate(id))
        );
      } catch (error) {
        console.error('Failed to unarchive bookings:', error);
        throw new Error('Failed to unarchive bookings');
      }
    }),

    createInvoice: async (bookingId, payload, options) => {
      await runWithInvoiceToasts(
        options,
        async () => {
          const result = await api.createInvoice(bookingId, payload);
          store.dispatch({
            type: BOOKING_ACTIONS.CREATE_INVOICES_SUCCESS,
            result,
            booking_id: bookingId
          });
          await awaitReportSystemEffects(
            messageService,
            getSystemEffectsForBookingUpdate(bookingId)
          );
        },
        'Document created',
        'Failed to create document'
      );
    },

    updateInvoice: async (bookingId, invoiceId, payload, options) => {
      await runWithInvoiceToasts(
        options,
        async () => {
          const result = await api.updateInvoice(
            bookingId,
            invoiceId,
            payload as Parameters<BookingApi['updateInvoice']>[2]
          );
          store.dispatch({
            type: BOOKING_ACTIONS.UPDATE_INVOICE_SUCCESS,
            result,
            booking_id: bookingId
          });
          await awaitReportSystemEffects(
            messageService,
            getSystemEffectsForBookingUpdate(bookingId)
          );
        },
        'Document saved',
        'Failed to save document'
      );
    },

    deleteInvoice: async (bookingId, invoiceId, options) => {
      await runWithInvoiceToasts(
        options,
        async () => {
          await api.deleteInvoice(bookingId, invoiceId);
          store.dispatch({
            type: BOOKING_ACTIONS.DELETE_INVOICE_SUCCESS,
            booking_id: bookingId,
            invoice_id: invoiceId
          });
          await awaitReportSystemEffects(
            messageService,
            getSystemEffectsForBookingUpdate(bookingId)
          );
        },
        'Document deleted',
        'Failed to delete document'
      );
    },

    createReceipt: async (expenseId, payload, options) => {
      await runWithInvoiceToasts(
        options,
        async () => {
          const result = await api.createReceipt(expenseId, payload);
          store.dispatch({
            type: EXPENSES_ACTIONS.CREATE_EXPENSE_RECEIPT_SUCCESS,
            result,
            expense_item_id: expenseId
          });
          await awaitReportSystemEffects(
            messageService,
            getSystemEffectsForExpenseUpdate(expenseId)
          );
        },
        'Document created',
        'Failed to create document'
      );
    },

    updateReceipt: async (expenseId, receiptId, payload, options) => {
      await runWithInvoiceToasts(
        options,
        async () => {
          const result = await api.updateReceipt(expenseId, receiptId, payload);
          store.dispatch({
            type: EXPENSES_ACTIONS.UPDATE_EXPENSE_RECEIPT_SUCCESS,
            result,
            expense_item_id: expenseId
          });
          await awaitReportSystemEffects(
            messageService,
            getSystemEffectsForExpenseUpdate(expenseId)
          );
        },
        'Document saved',
        'Failed to save document'
      );
    },

    deleteReceipt: async (expenseId, receiptId, options) => {
      await runWithInvoiceToasts(
        options,
        async () => {
          await api.deleteReceipt(expenseId, receiptId);
          store.dispatch({
            type: EXPENSES_ACTIONS.DELETE_EXPENSE_RECEIPT_SUCCESS,
            expense_item_id: expenseId,
            receipt: receiptId
          });
          await awaitReportSystemEffects(
            messageService,
            getSystemEffectsForExpenseUpdate(expenseId)
          );
        },
        'Document deleted',
        'Failed to delete document'
      );
    }
  };
}
